/**
 * Asking for a summary on purpose, and seeing what the asking has cost.
 *
 * Three ways a bookmark reaches the enrichment queue: a save puts it there
 * ([SaveBookmarkRequest.enrich]), a person asks for one row again
 * ([ApiRoutes.bookmarkSummarise]), or a person asks for everything that has never been
 * tried ([ApiRoutes.BOOKMARKS_SUMMARISE_MISSING]). The first is a side effect of saving
 * and lives with the save; the other two are somebody pressing a button, and they live
 * here with the numbers that say what the pressing did.
 *
 * ── Both of the deliberate ones can be refused, and the refusal is the useful part ──
 *
 * A server with no `LUNAPIN_KEY_OPENAI` and an account with both AI switches off are two
 * different problems with two different people who can fix them — the operator and the
 * owner — and a route that quietly enqueued work that will never run would report neither.
 * So both refuse, both say which of the two it is in a sentence written to be shown as-is,
 * and they use different statuses so a client can tell them apart without reading English:
 * **503** for the deployment's missing key, matching what [ApiRoutes.AUTH_SIGN_IN] answers
 * for a deployment with no credentials, and **409** for the account's own switches, which
 * is a state the person asking can change themselves.
 *
 * ── And watching the second of them, and stopping it ────────────────────────
 *
 * A backfill over four thousand bookmarks runs for hours, so the press is only half of it:
 * [EnrichmentProgress] is what a panel counts down while it runs, and
 * [CancelBackfillResult] is what stops it. Both are here rather than beside the collection's
 * types because they describe the same act as [SummariseMissingResult] does — the queue,
 * from the outside — and because a progress count that lived somewhere else would be a
 * second opinion about what "still to do" means.
 *
 * @see ApiRoutes.bookmarkSummarise
 * @see ApiRoutes.BOOKMARKS_SUMMARISE_MISSING
 * @see ApiRoutes.AI_PROGRESS
 * @see ApiRoutes.AI_CANCEL_BACKFILL
 */
package se.soderbjorn.lunapin.clientserver

import kotlinx.serialization.Serializable

/**
 * What a backfill did.
 *
 * @property enqueued how many bookmarks were put in the queue. Counted by the database as
 *   it wrote them rather than by a count taken before or after, either of which could
 *   disagree with what actually happened.
 *
 *   **Zero is an ordinary answer**, not a failure: it means every bookmark this account has
 *   was already summarised, already queued, or already tried and given up on. A caller
 *   should say so in words rather than showing a spinner for work that is not happening.
 */
@Serializable
data class SummariseMissingResult(
    val enqueued: Long = 0,
)

/**
 * How far this account's enrichment queue has got, right now.
 *
 * What the backfill panel draws while a backfill is running, polled every couple of seconds
 * — see [ApiRoutes.AI_PROGRESS], where the polling is argued against subscribing to the
 * event stream.
 *
 * ── Every number here is counted, and none of them is remembered ─────────────
 *
 * **"A backfill is running" is [isRunning], derived from the counts**, and that is the whole
 * point of this type. The client flag it replaces meant "the enqueue request is in flight",
 * which flipped false the millisecond the POST answered — before a single page had been read
 * — and knew nothing at all about a backfill started in another browser, before a reload, or
 * before the server last restarted. A count of the queue is right in every one of those, and
 * there is no client state left to drift.
 *
 * What is deliberately *not* here is a percentage, because a bar needs a baseline — how many
 * were queued when the run started — and nothing stores one. It could not be recovered after
 * a restart, and storing it would be a migration for a cosmetic. A remaining count ticking
 * down is honest and survives everything.
 *
 * @property pending how many are in the queue. `ai_state = 'pending'` **is** the queue; see
 *   the server's `Bookmarks.sq`.
 * @property running how many the worker has claimed. At most one today, because there is one
 *   worker — carried as a number rather than a boolean so that a second worker is a
 *   configuration change and not a wire change.
 * @property done how many have been read and summarised, ever. Not "this month" and not "in
 *   this run": the column records a state rather than a history, and there is nothing in it
 *   that knows which backfill put a row there.
 * @property failed how many were tried and given up on, ever. Shown beside the remaining
 *   count because it is where a *Try these again* button eventually belongs —
 *   `enqueueMissingForAi` deliberately will not pick up a row that spent its three attempts,
 *   so a backfill can finish with a dozen bookmarks nobody has been told about.
 * @property awaitingSummary how many a backfill would enqueue if it were pressed now — the
 *   same number [AiUsage.awaitingSummary] carries, from the same server-side count, because
 *   the idle half of the panel is labelled with it and this is the response that keeps the
 *   panel live. Reading it from the month's tally instead would leave the idle line stating a
 *   number from whenever the window was opened: cancel a backfill and it would be zero,
 *   finish one started on a phone and it would still be four thousand.
 */
@Serializable
data class EnrichmentProgress(
    val pending: Long = 0,
    val running: Long = 0,
    val done: Long = 0,
    val failed: Long = 0,
    val awaitingSummary: Long = 0,
) {
    /** How many bookmarks are still to be read — the number the panel counts down. */
    val left: Long get() = pending + running

    /** Whether a backfill is happening. The counts say so; nothing else is consulted. */
    val isRunning: Boolean get() = left > 0
}

/**
 * What a cancel did.
 *
 * @property cancelled how many bookmarks were taken out of the queue, counted by the database
 *   as it wrote them — [SummariseMissingResult.enqueued]'s reason exactly, and the two are a
 *   pair: one says how many were put in, this says how many came back out.
 *
 *   **Zero is an ordinary answer.** The queue drained between the panel deciding to offer a
 *   *Cancel* and the press landing, which is a race nobody can close and which costs nothing:
 *   the next poll shows an idle panel either way.
 */
@Serializable
data class CancelBackfillResult(
    val cancelled: Long = 0,
)

/**
 * How much this account has asked the model for this month, and what came back.
 *
 * What the settings screen's usage panel draws. Four numbers and the month they belong to,
 * counted by the server as the work happens — see the server's `AiUsage.sq` for why two of
 * them cannot be derived afterwards at any price.
 *
 * @property month the month these numbers are for, as `YYYYMM` — `202608`. **Answered
 *   rather than asked for.** A caller naming a month would mean a browser and a background
 *   worker in different zones writing into and reading out of different rows on the last
 *   evening of every month; the server picks one (UTC) and says which. A screen formats it
 *   for the reader.
 * @property summaries how many bookmarks were given a generated summary. Not "how many
 *   were enriched": an account with summaries off and tags on enriches bookmarks and
 *   writes no summaries.
 * @property tagsAdded how many tags the model actually put on a bookmark — after the cap,
 *   after de-duplication, and not counting one that was already there. What was *offered*
 *   is counted nowhere: tags are applied rather than proposed, so an offer that was
 *   filtered out never existed as far as the collection is concerned.
 * @property newTags of [tagsAdded], how many were words this account had never used
 *   anywhere. **The number the panel is for** — the one `aiTagVocabularyTarget` is chosen
 *   against, and the honest replacement for an earlier design's *Accepted 61%*, which
 *   measured approvals of proposals that no longer exist. Always `<= tagsAdded`; the
 *   difference is "matched a word you already use".
 * @property failedFetches how many enrichments gave up this month. One counter rather than
 *   one per cause: a panel cannot act on the difference between a page behind a login and
 *   a provider having a bad afternoon, whereas the sentence on the row can and does.
 * @property awaitingSummary how many bookmarks a backfill would enqueue **right now**.
 *   Not a usage number and not about this month — it is here because it is the one thing a
 *   *Summarise 412 older bookmarks* button needs before it is pressed, and a route of its
 *   own to answer one integer beside a panel that is being fetched anyway would be a
 *   second request for the same screen.
 *
 *   **The settings panel no longer reads it from here**, and this response is answered once
 *   per window rather than polled: the same count rides on [EnrichmentProgress], where it
 *   arrives beside the queue's own numbers every couple of seconds, so the panel's two halves
 *   cannot disagree about which state it is in. Both come from one server-side count, so this
 *   is a second reader rather than a second opinion. It stays because a caller that wants the
 *   number without polling has nowhere else to get it.
 */
@Serializable
data class AiUsage(
    val month: String = "",
    val summaries: Long = 0,
    val tagsAdded: Long = 0,
    val newTags: Long = 0,
    val failedFetches: Long = 0,
    val awaitingSummary: Long = 0,
)
